import { FC, ReactNode, useMemo } from 'react';
import Image from "next/image";
import { capitalize, titleCase } from 'functions/capitalize';
import styles from "./CategoryBanner.module.css";
import { imageLoader } from 'functions/imageLoader';
import Spinner from 'components/Spinner/Spinner';
import Bag from 'components/Icons/Bag';
import ShopIcon from 'components/Icons/ShopIcon';
import ShoppingIconNoCircle from 'components/Icons/ShoppingIconNoCircle';
import router, { useRouter } from 'next/router';
import React from 'react';
import Breadcrumb, { BreadcrumbItem } from 'components/Breadcrumb/Breadcrumb';
import Head from 'next/head';
import { getCategoryTitle } from 'functions/categoryTitle';
import { changeSunglasses } from 'functions/changeSunglasses';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';

export interface CategoryBannerProps {
  title: string;
  description: string;
  imageSrc: string;
  imageAlt?: string;
  videoSrc?: string;
  children?: ReactNode;
  breadcrumbItems?: BreadcrumbItem[];
  // Additional props like colour etc.
  [x: string]: any;
}

const CategoryBanner: FC<CategoryBannerProps> = ({
  title,
  description,
  imageSrc,
  imageAlt,
  videoSrc,
  children,
  breadcrumbItems,
  ...props
}) => {
  const router = useRouter();
  const canReorderContactLens = useSelector((state: RootState) => state.authEvent.canReorderContactLens);
  const showDescription = useMemo(() => {
    return !router.pathname.includes('colour') && !(router.query.colour && router.query.colour.length > 0);
  }, [router]);

  const isFramePage = useMemo(() => {
    return router.pathname.includes('frames');
  }, [router]);

  const mutatedDescription = useMemo(() => {
    //console.log('props', title, description, breadcrumbItems);
    const newTitle = getCategoryTitle({ ...props, query: router.query });

    // if (!showDescription) {
    //   return `<h1>${breadcrumbItems?.[breadcrumbItems.length - 1]?.text}</h1>`;
    // }

    if (!newTitle) {
      return null;
    }

    if ((description.trim() == '' && breadcrumbItems?.[breadcrumbItems.length - 1]?.text)) {
      return `<h1>${breadcrumbItems?.[breadcrumbItems.length - 1]?.text}</h1>`;
    }
    /*
        if(title) {
          //Replace text in description between <h1> tags with title remove old title
          const newDescription = description.replace(/<h1>.*<\/h1>/, `<h1>${title}</h1>`);
          return newDescription;
        }*/
    if (router.query.category) {

      let newDescription = description.replace(/<h1>(.*)<\/h1>/, `<h1>$1</h1>`);
      //append newTitle to end of h1 tag at the end of the string, keep old title with regex group
      if (router.query.shape && router.query.shape == 'round') {
        newDescription = newDescription.replace('Capote', ' - Capote Frames');
      }
      return newDescription;
    }


    return description.replace('<h1>', `<h1>${newTitle} `);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.colour, props.type, props.brand, props.frequency, router.query.colour, router.query.shape, showDescription]);

  // Remove html tags from alt
  const mutatedImageAlt = useMemo(() => {
    if (imageAlt) {
      return imageAlt;
    }

    return title.replace(/(<([^>]+)>)/gi, " ")
  }, [title, imageAlt]);


  const isContactLensPage = useMemo(() => {
    return title.includes('Contact Lenses');
  }, [title]);


  const posterSrc = videoSrc ? videoSrc.replace('.mp4', '.jpg') : null;


  return (
    <React.Fragment>
      <Head>
        {imageSrc && (
          <>
            <link
              rel="preload"
              href={`${imageSrc}?w=1200&q=90`}
              as="image"
              media="(max-width: 1023px)"
            />
            <link
              rel="preload"
              href={`${imageSrc}?w=1920&q=90`}
              as="image"
              media="(min-width: 1024px)"
            />
          </>
        )}
        {videoSrc && (
          <link
            rel="preload"
            href={posterSrc!}
            as="image"
            type="image/jpeg"
          />
        )}
      </Head>

      <section
        className="relative w-full bg-[#FFF4D8]"
        key={imageSrc}
      >
        <div className="relative w-full h-0 pb-[50.00%] lg:pb-[30.86%]"> {/* 21:9 aspect ratio */}
          {videoSrc && (
            <video
              autoPlay
              muted
              loop
              poster={posterSrc!}
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
              src={videoSrc}
            >
              <source src={videoSrc} type="video/mp4" />
              <meta itemProp="description" content={title} />
            </video>
          )}
          {!videoSrc && (<Image
            quality={90}
            fill
            style={{
              objectFit: "cover",
              objectPosition: "center",
            }}
            loader={imageLoader}
            sizes="100vw"
            priority={true}
            src={imageSrc!}
            alt={mutatedImageAlt}
          />)}
        </div>
      </section>
      <div className="mx-auto max-w-[1224px] bg-white px-4 pt-4">
        {breadcrumbItems && (
          <span className="md:flex">
            <Breadcrumb data={breadcrumbItems!} />
          </span>
        )}
        <div className='flex flex-col mt-6'>
          <div
            className={styles.banner__header}
            dangerouslySetInnerHTML={{
              __html: mutatedDescription ? mutatedDescription : description
            }}
          />

          {isContactLensPage && canReorderContactLens && (
            <div className='flex justify-center w-full mb-[5px] cursor-pointer' onClick={() => {
              router.push('/re-order-contact-lenses')
            }}>
              <div className="flex items-center justify-center pl-3 pr-6 py-1 rounded-lg border border-black bg-transparent hover:bg-white/10 transition-colors duration-200">
                <ShoppingIconNoCircle fill='#000' className="mr-3 w-6 h-6" />
                <span className="text-base font-medium">Re-order Contact Lenses</span>
              </div>
            </div>
          )}

        </div>
        {children}
      </div>
    </React.Fragment>
  );
};

export default CategoryBanner;
